import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import OnboardingPayment from "@images/payment/onboardingPayment.svg"
import OnboardingSuccess from "@images/payment/onboardingSuccess.svg"
import { AlertIconDark } from "@images/svgIcons/alertIcon"
import { SmallArrowIcon } from "@images/svgIcons/arrowIcon"
import { BoxDetailsEntity } from "@modules/box/entities/boxEntity"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useStripeAccount } from "@modules/finances/hooks/useStripeAccount"
import { StripeAccountErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { getStripeAccountErrorText } from "@modules/finances/utils/financeUtils"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import type { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { AdvantagesView } from "@modules/ui/components/advantagesView"
import { Loader } from "lucide-react"

import styled from "styled-components"

type BoxBankingProps = {
  box: BoxDetailsEntity
  className?: string
} & React.ComponentProps<"div">

export function BoxBanking({ box, className, ...props }: BoxBankingProps) {
  const { updateStripeAccount, loading: actionLoading } = useStripeAccount()
  const boxNavigate = useBoxNavigate()
  const canSeeBankDetails = useBoxStaffPermission(BoxStaffPermissions.SEE_BANK_DETAILS)
  const canEditBankDetails = useBoxStaffPermission(BoxStaffPermissions.EDIT_BANK_DETAILS)

  const { title, description, textButton, iconButton } = getScreenWording(box.stripeAccountStatus, box.accountError)
  const advantages = [
    word("box.settings.informations.line_1"),
    word("box.settings.informations.line_2"),
    word("box.settings.informations.line_3"),
    word("box.settings.informations.line_4"),
  ]

  if (!canSeeBankDetails) {
    boxNavigate(Path.Box.Planning)
  }

  return (
    <div className={cn("flex flex-col justify-center gap-7 xl:flex-row", className)} {...props}>
      <CardAccount>
        <div className="flex flex-col items-center gap-10">
          <img
            style={{
              marginBottom: box.stripeAccountStatus === "OK" && !box.accountError ? -28 : 0,
              maxWidth: 200,
            }}
            src={box.stripeAccountStatus === "OK" && !box.accountError ? OnboardingSuccess : OnboardingPayment}
            alt=""
          />
          <div className="max-w-90% flex flex-col gap-4 text-center">
            <Text fontStyle="HL" className="flex flex-row items-center gap-2">
              {box.accountError && <AlertIconDark color="currentColor" className="text-error" />}
              {title}
            </Text>

            <Text fontStyle="BM" style={{ whiteSpace: "pre-line" }}>
              {description}
            </Text>
          </div>

          <Button
            variant="primary"
            onClick={() => {
              if (!canEditBankDetails) {
                return
              }
              if (!box.stripeAccountStatus || box.stripeAccountStatus === "UNDEFINED") {
                boxNavigate(Path.Box.OnboardingStripe)
              } else {
                void updateStripeAccount()
              }
            }}
            disabled={!canEditBankDetails || actionLoading}
          >
            {textButton}
            {actionLoading ? (
              <Loader className="animate-spin" />
            ) : (
              iconButton && <SmallArrowIcon className="ml-2" color="currentColor" />
            )}
          </Button>
        </div>
      </CardAccount>

      <AdvantagesViewStyled
        fontSizeBig
        title={word("box.settings.informations.title")}
        description={word("box.settings.informations.description")}
        subtitle={word("box.settings.informations.subtitle")}
        advantages={advantages}
      />
    </div>
  )
}
const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 60px 40px;
  flex: 1;
  border-radius: 12px;
`

const AdvantagesViewStyled = styled(AdvantagesView)`
  padding: 60px 40px;
  flex: 1;
  border-radius: 12px;
`

const CardAccount = styled(Card)`
  background-color: var(--surface-background);
  align-items: center;
  justify-content: center;
  > div {
    width: 100%;
  }
  img {
    max-width: 80%;
  }
`

const getScreenWording = (
  status: StripeAccountStatus | undefined,
  stripeAccountError?: StripeAccountErrorCode,
): { title: string; description: string; textButton: string; iconButton: boolean } => {
  if (!status || status === "UNDEFINED") {
    return {
      title: word("box.settings.account.undefined.title"),
      description: word("box.settings.account.undefined.description"),
      textButton: word("box.settings.button.start.label"),
      iconButton: true,
    }
  }
  if (status === "REQUIRE_ADDITIONAL_INFORMATION" || status === "ONBOARDING") {
    return {
      title: word("box.settings.account.need_information.title"),
      description: word("box.settings.account.need_information.description"),
      textButton: word("box.settings.button.start.label"),
      iconButton: true,
    }
  }
  if (stripeAccountError) {
    const stripeAccountErrorText = getStripeAccountErrorText(stripeAccountError)
    return {
      title: stripeAccountErrorText.alertTitle,
      description: stripeAccountErrorText.alertContent,
      textButton: stripeAccountErrorText.primaryButtonLabel,
      iconButton: true,
    }
  }
  if (status === "OK") {
    return {
      title: word("box.settings.account.ok.title"),
      description: word("box.settings.account.ok.description"),
      textButton: word("box.settings.button.update.label"),
      iconButton: false,
    }
  }
  return {
    title: "",
    description: "",
    textButton: "",
    iconButton: false,
  }
}
